import React, { useMemo, useState } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { PlayIcon, StopCircleIcon, SearchIcon } from 'lucide-react';
import { beneficiaries, Beneficiary } from '../data/beneficiaries';
// Mock vessel data for BLC lookup
const vesselData: Record<string, {
  name: string;
  type: string;
  regNo: string;
  owner: string;
  length: string;
  breadth: string;
  depth: string;
  weightCapacity: string;
  engineType: string;
  enginePower: string;
  status: string;
}> = {
  'VSL-001': {
    name: 'MV Sundarban-1',
    type: 'Boat',
    regNo: 'REG-2024-001',
    owner: 'Rahim Uddin',
    length: '32',
    breadth: '8',
    depth: '4',
    weightCapacity: '5.2',
    engineType: 'Diesel',
    enginePower: '120 HP',
    status: 'Active'
  },
  'VSL-002': {
    name: 'FV Padma-3',
    type: 'Trawler',
    regNo: 'REG-2023-089',
    owner: 'Karim Sheikh',
    length: '45',
    breadth: '12',
    depth: '6',
    weightCapacity: '12.5',
    engineType: 'Diesel',
    enginePower: '250 HP',
    status: 'Active'
  },
  'VSL-003': {
    name: 'BT Meghna-7',
    type: 'Launch',
    regNo: 'REG-2024-015',
    owner: 'Abdul Hakim',
    length: '55',
    breadth: '14',
    depth: '7',
    weightCapacity: '20.0',
    engineType: 'Diesel',
    enginePower: '350 HP',
    status: 'Active'
  }
};
export function HarvestManagement() {
  const [activeTab, setActiveTab] = useState<'applications' | 'apply' | 'tracking' | 'history' | 'startTrip'>('applications');
  const [currentStep, setCurrentStep] = useState(0);
  // Beneficiary search state
  const [beneficiarySearch, setBeneficiarySearch] = useState('');
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<Beneficiary | null>(null);
  const filteredBeneficiaries = useMemo(() => {
    if (!beneficiarySearch.trim()) return [];
    const term = beneficiarySearch.toLowerCase();
    return beneficiaries.filter(b => b.name.toLowerCase().includes(term) || b.nid.includes(term) || b.phone.includes(term)).slice(0, 5);
  }, [beneficiarySearch]);
  // Apply Permit Form State
  const [applyForm, setApplyForm] = useState({
    permitType: '',
    duration: '',
    crewCount: '',
    forestZone: '',
    purpose: '',
    blcNo: ''
  });
  const selectedVessel = applyForm.blcNo ? vesselData[applyForm.blcNo] : null;
  // Start Trip Form State
  const [startTripForm, setStartTripForm] = useState({
    permitId: '',
    startLocation: '',
    startDate: new Date().toISOString().split('T')[0],
    startTime: new Date().toTimeString().slice(0, 5)
  });
  // Data states
  const [applications, setApplications] = useState([{
    permitId: 'HRV-2024-001',
    beneficiaryName: 'মোঃ আঃ গনি গাজী',
    type: 'Fishing',
    appliedDate: '2024-03-28',
    duration: '7 days',
    status: 'pending' as const
  }, {
    permitId: 'HRV-2024-002',
    beneficiaryName: 'আলাউদ্দীন শেখ',
    type: 'Honey Collection',
    appliedDate: '2024-03-27',
    duration: '14 days',
    status: 'approved' as const
  }, {
    permitId: 'HRV-2024-003',
    beneficiaryName: 'পরিমল ঢালী',
    type: 'Wood Collection',
    appliedDate: '2024-03-26',
    duration: '10 days',
    status: 'approved' as const
  }]);
  const [activeTrips, setActiveTrips] = useState([{
    name: 'আলাউদ্দীন শেখ',
    vessel: 'MV Sundarban-1',
    zone: 'Chandpai Range',
    entryTime: '2024-03-30 06:00',
    elapsed: '4h 30m'
  }, {
    name: 'পরিমল ঢালী',
    vessel: 'FV Padma-3',
    zone: 'Sarankhola Range',
    entryTime: '2024-03-30 05:30',
    elapsed: '5h 00m'
  }]);
  const [tripHistory, setTripHistory] = useState([{
    tripId: 'TRIP-HRV-001',
    name: 'মোঃ আঃ গনি গাজী',
    vessel: 'BT Meghna-7',
    zone: 'Chandpai Range',
    entry: '2024-03-29 06:00',
    exit: '2024-03-29 18:30',
    duration: '12h 30m',
    status: 'approved' as const
  }]);
  const approvedPermits = applications.filter(p => p.status === 'approved');
  const selectedPermit = approvedPermits.find(p => p.permitId === startTripForm.permitId);
  const applicationColumns = [{
    key: 'permitId',
    label: 'Permit ID'
  }, {
    key: 'beneficiaryName',
    label: 'Beneficiary Name'
  }, {
    key: 'type',
    label: 'Type'
  }, {
    key: 'appliedDate',
    label: 'Applied Date'
  }, {
    key: 'duration',
    label: 'Duration'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-995" />
  }];
  const historyColumns = [{
    key: 'tripId',
    label: 'Trip ID'
  }, {
    key: 'name',
    label: 'Beneficiary'
  }, {
    key: 'vessel',
    label: 'Vessel'
  }, {
    key: 'zone',
    label: 'Zone'
  }, {
    key: 'entry',
    label: 'Entry Time'
  }, {
    key: 'exit',
    label: 'Exit Time'
  }, {
    key: 'duration',
    label: 'Duration'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-996" />
  }];
  const filters = [{
    key: 'type',
    label: 'Permit Type',
    options: ['Fishing', 'Honey Collection', 'Wood Collection']
  }, {
    key: 'status',
    label: 'Status',
    options: ['Pending', 'Approved', 'Rejected']
  }];
  const wizardSteps = [{
    title: 'Select Beneficiary',
    description: 'Search and select beneficiary'
  }, {
    title: 'Permit Details',
    description: 'Enter permit information'
  }, {
    title: 'Vessel & BLC',
    description: 'Enter BLC number for vessel info'
  }, {
    title: 'Review & Submit',
    description: 'Review all information before submitting'
  }];
  return <div className="space-y-6" data-id="element-997">
      <div data-id="element-998">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-999">Harvest Management</h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-1000">
          Manage harvest permits (fishing, honey, wood) and track trips
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-1001">
        <div className="flex gap-4" data-id="element-1002">
          <button onClick={() => setActiveTab('applications')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'applications' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1003">
            Harvest Applications
          </button>
          <button onClick={() => setActiveTab('apply')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'apply' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1004">
            Apply for Harvest Permit
          </button>
          <button onClick={() => setActiveTab('tracking')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'tracking' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1005">
            Trip Tracking ({activeTrips.length})
          </button>
          <button onClick={() => setActiveTab('history')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'history' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1006">
            Trip History
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'applications' && <DataTable columns={applicationColumns} data={applications} filters={filters} onDataChange={setApplications} searchPlaceholder="Search harvest permits..." data-id="element-1007" />}

      {activeTab === 'apply' && <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={setCurrentStep} onSubmit={() => {
      if (!selectedBeneficiary || !applyForm.permitType) {
        alert('Please select a beneficiary and permit type.');
        return;
      }
      const newPermit = {
        permitId: `HRV-2024-${String(applications.length + 1).padStart(3, '0')}`,
        beneficiaryName: selectedBeneficiary.name,
        type: applyForm.permitType,
        appliedDate: new Date().toISOString().split('T')[0],
        duration: applyForm.duration ? `${applyForm.duration} days` : 'Not specified',
        status: 'pending' as const
      };
      setApplications([newPermit, ...applications]);
      alert('Harvest permit application submitted successfully!');
      setCurrentStep(0);
      setActiveTab('applications');
      // Reset form
      setApplyForm({
        permitType: '',
        duration: '',
        crewCount: '',
        forestZone: '',
        purpose: '',
        blcNo: ''
      });
      setSelectedBeneficiary(null);
      setBeneficiarySearch('');
    }} data-id="element-1008">
          {currentStep === 0 && <div className="space-y-4" data-id="element-1009">
              <div data-id="element-1010">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1011">
                  Search Beneficiary *
                </label>
                <div className="relative" data-id="element-1012">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-1013" />
                  <input type="text" placeholder="Enter NID, Name, or Phone" value={beneficiarySearch} onChange={e => {
              setBeneficiarySearch(e.target.value);
              if (selectedBeneficiary) setSelectedBeneficiary(null);
            }} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1014" />
                </div>
              </div>

              {/* Search Results */}
              {!selectedBeneficiary && beneficiarySearch.trim() && <div className="space-y-2" data-id="element-1015">
                  {filteredBeneficiaries.length > 0 ? filteredBeneficiaries.map((b, idx) => <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-[#1B5E20] transition-colors" data-id="element-1016">
                        <div className="flex items-center gap-4" data-id="element-1017">
                          <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center text-xl" data-id="element-1018">
                            {b.photo}
                          </div>
                          <div className="flex-1" data-id="element-1019">
                            <h4 className="font-semibold text-gray-900" data-id="element-1020">
                              {b.name}
                            </h4>
                            <div className="text-sm text-gray-600 mt-0.5 flex gap-4" data-id="element-1021">
                              <span data-id="element-1022">NID: {b.nid}</span>
                              <span data-id="element-1023">Phone: {b.phone}</span>
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-1024">
                              {b.profession} • {b.village}
                            </div>
                          </div>
                          <button type="button" onClick={() => {
                setSelectedBeneficiary(b);
                setBeneficiarySearch(b.name);
              }} className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] text-sm font-medium" data-id="element-1025">
                            Select
                          </button>
                        </div>
                      </div>) : <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center text-sm text-gray-500" data-id="element-1026">
                      No beneficiaries found matching "{beneficiarySearch}"
                    </div>}
                </div>}

              {/* Selected Beneficiary */}
              {selectedBeneficiary && <div className="bg-green-50 rounded-lg p-4 border border-green-200" data-id="element-1027">
                  <div className="flex items-center gap-4" data-id="element-1028">
                    <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center text-2xl" data-id="element-1029">
                      {selectedBeneficiary.photo}
                    </div>
                    <div className="flex-1" data-id="element-1030">
                      <div className="flex items-center gap-2" data-id="element-1031">
                        <h4 className="font-semibold text-gray-900" data-id="element-1032">
                          {selectedBeneficiary.name}
                        </h4>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-1033">
                          Selected
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1 grid grid-cols-2 gap-1" data-id="element-1034">
                        <div data-id="element-1035">NID: {selectedBeneficiary.nid}</div>
                        <div data-id="element-1036">Phone: {selectedBeneficiary.phone}</div>
                        <div data-id="element-1037">Profession: {selectedBeneficiary.profession}</div>
                        <div data-id="element-1038">Village: {selectedBeneficiary.village}</div>
                      </div>
                    </div>
                    <button type="button" onClick={() => {
              setSelectedBeneficiary(null);
              setBeneficiarySearch('');
            }} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium" data-id="element-1039">
                      Change
                    </button>
                  </div>
                </div>}
            </div>}

          {currentStep === 1 && <div className="space-y-4" data-id="element-1040">
              <div data-id="element-1041">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1042">
                  Permit Type *
                </label>
                <select value={applyForm.permitType} onChange={e => setApplyForm({
            ...applyForm,
            permitType: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1043">
                  <option value="" data-id="element-1044">Select Type</option>
                  <option value="Fishing" data-id="element-1045">Fishing</option>
                  <option value="Honey Collection" data-id="element-1046">Honey Collection</option>
                  <option value="Wood Collection" data-id="element-1047">Wood Collection</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-1048">
                <div data-id="element-1049">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1050">
                    Requested Duration (Days) *
                  </label>
                  <input type="number" value={applyForm.duration} onChange={e => setApplyForm({
              ...applyForm,
              duration: e.target.value
            })} placeholder="e.g. 7" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1051" />
                </div>
                <div data-id="element-1052">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1053">
                    Number of Crew *
                  </label>
                  <input type="number" value={applyForm.crewCount} onChange={e => setApplyForm({
              ...applyForm,
              crewCount: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1054" />
                </div>
              </div>
              <div data-id="element-1055">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1056">
                  Forest Zone/Area *
                </label>
                <select value={applyForm.forestZone} onChange={e => setApplyForm({
            ...applyForm,
            forestZone: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1057">
                  <option value="" data-id="element-1058">Select Zone</option>
                  <option value="Chandpai Range" data-id="element-1059">Chandpai Range</option>
                  <option value="Sarankhola Range" data-id="element-1060">Sarankhola Range</option>
                  <option value="Khulna Range" data-id="element-1061">Khulna Range</option>
                  <option value="Satkhira Range" data-id="element-1062">Satkhira Range</option>
                </select>
              </div>
              <div data-id="element-1063">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1064">
                  Purpose
                </label>
                <textarea rows={3} value={applyForm.purpose} onChange={e => setApplyForm({
            ...applyForm,
            purpose: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1065"></textarea>
              </div>
            </div>}

          {currentStep === 2 && <div className="space-y-6" data-id="element-1066">
              <div data-id="element-1067">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1068">
                  BLC No. *
                </label>
                <input type="text" placeholder="e.g. VSL-001" value={applyForm.blcNo} onChange={e => setApplyForm({
            ...applyForm,
            blcNo: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1069" />
                <p className="text-xs text-gray-500 mt-1" data-id="element-1070">
                  Enter a valid BLC number to retrieve vessel information. Try
                  VSL-001, VSL-002, VSL-003.
                </p>
              </div>

              {selectedVessel && <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1071">
                  <h4 className="font-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2" data-id="element-1072">
                    Vessel Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-1073">
                    <div data-id="element-1074">
                      <span className="text-gray-600 block mb-1" data-id="element-1075">
                        Vessel Name
                      </span>
                      <span className="font-medium" data-id="element-1076">{selectedVessel.name}</span>
                    </div>
                    <div data-id="element-1077">
                      <span className="text-gray-600 block mb-1" data-id="element-1078">
                        Vessel Type
                      </span>
                      <span className="font-medium" data-id="element-1079">{selectedVessel.type}</span>
                    </div>
                    <div data-id="element-1080">
                      <span className="text-gray-600 block mb-1" data-id="element-1081">
                        Registration No
                      </span>
                      <span className="font-medium" data-id="element-1082">
                        {selectedVessel.regNo}
                      </span>
                    </div>
                    <div data-id="element-1083">
                      <span className="text-gray-600 block mb-1" data-id="element-1084">Owner</span>
                      <span className="font-medium" data-id="element-1085">
                        {selectedVessel.owner}
                      </span>
                    </div>
                    <div data-id="element-1086">
                      <span className="text-gray-600 block mb-1" data-id="element-1087">
                        Dimensions (L×B×D)
                      </span>
                      <span className="font-medium" data-id="element-1088">
                        {selectedVessel.length}' × {selectedVessel.breadth}' ×{' '}
                        {selectedVessel.depth}'
                      </span>
                    </div>
                    <div data-id="element-1089">
                      <span className="text-gray-600 block mb-1" data-id="element-1090">
                        Engine Info
                      </span>
                      <span className="font-medium" data-id="element-1091">
                        {selectedVessel.engineType} (
                        {selectedVessel.enginePower})
                      </span>
                    </div>
                  </div>
                </div>}
            </div>}

          {currentStep === 3 && <div className="space-y-6" data-id="element-1092">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-1093">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-1094">
                  Beneficiary
                </h4>
                <div className="text-sm" data-id="element-1095">
                  {selectedBeneficiary ? <div className="grid grid-cols-2 gap-2" data-id="element-1096">
                      <div data-id="element-1097">
                        <span className="text-gray-600" data-id="element-1098">Name:</span>{' '}
                        <span className="font-medium" data-id="element-1099">
                          {selectedBeneficiary.name}
                        </span>
                      </div>
                      <div data-id="element-1100">
                        <span className="text-gray-600" data-id="element-1101">NID:</span>{' '}
                        <span className="font-medium" data-id="element-1102">
                          {selectedBeneficiary.nid}
                        </span>
                      </div>
                      <div data-id="element-1103">
                        <span className="text-gray-600" data-id="element-1104">Phone:</span>{' '}
                        <span className="font-medium" data-id="element-1105">
                          {selectedBeneficiary.phone}
                        </span>
                      </div>
                      <div data-id="element-1106">
                        <span className="text-gray-600" data-id="element-1107">Profession:</span>{' '}
                        <span className="font-medium" data-id="element-1108">
                          {selectedBeneficiary.profession}
                        </span>
                      </div>
                    </div> : <span className="text-gray-500" data-id="element-1109">
                      No beneficiary selected
                    </span>}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-1110">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-1111">
                  Permit Details
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-1112">
                  <div data-id="element-1113">
                    <span className="text-gray-600" data-id="element-1114">Type:</span>{' '}
                    <span className="font-medium" data-id="element-1115">
                      {applyForm.permitType || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-1116">
                    <span className="text-gray-600" data-id="element-1117">Duration:</span>{' '}
                    <span className="font-medium" data-id="element-1118">
                      {applyForm.duration ? `${applyForm.duration} days` : 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-1119">
                    <span className="text-gray-600" data-id="element-1120">Zone:</span>{' '}
                    <span className="font-medium" data-id="element-1121">
                      {applyForm.forestZone || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-1122">
                    <span className="text-gray-600" data-id="element-1123">Crew:</span>{' '}
                    <span className="font-medium" data-id="element-1124">
                      {applyForm.crewCount ? `${applyForm.crewCount} persons` : 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-1125">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-1126">
                  Vessel Information
                </h4>
                <div className="text-sm" data-id="element-1127">
                  {selectedVessel ? <div className="grid grid-cols-2 gap-3" data-id="element-1128">
                      <div data-id="element-1129">
                        <span className="text-gray-600" data-id="element-1130">BLC No:</span>{' '}
                        <span className="font-medium" data-id="element-1131">{applyForm.blcNo}</span>
                      </div>
                      <div data-id="element-1132">
                        <span className="text-gray-600" data-id="element-1133">Vessel Name:</span>{' '}
                        <span className="font-medium" data-id="element-1134">
                          {selectedVessel.name}
                        </span>
                      </div>
                      <div data-id="element-1135">
                        <span className="text-gray-600" data-id="element-1136">Type:</span>{' '}
                        <span className="font-medium" data-id="element-1137">
                          {selectedVessel.type}
                        </span>
                      </div>
                      <div data-id="element-1138">
                        <span className="text-gray-600" data-id="element-1139">Owner:</span>{' '}
                        <span className="font-medium" data-id="element-1140">
                          {selectedVessel.owner}
                        </span>
                      </div>
                    </div> : <span className="text-gray-500" data-id="element-1141">No vessel selected</span>}
                </div>
              </div>
            </div>}
        </WizardForm>}

      {activeTab === 'tracking' && <div className="space-y-4" data-id="element-1142">
          <div className="flex justify-end" data-id="element-1143">
            <button onClick={() => setActiveTab('startTrip')} className="flex items-center gap-2 px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32]" data-id="element-1144">
              <PlayIcon size={16} data-id="element-1145" />
              Start New Trip
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1146">
            {activeTrips.map((trip, idx) => <div key={idx} className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1147">
                <div className="flex items-center justify-between mb-3" data-id="element-1148">
                  <h4 className="font-semibold text-gray-900" data-id="element-1149">{trip.name}</h4>
                  <div className="flex items-center gap-1" data-id="element-1150">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" data-id="element-1151"></div>
                    <span className="text-xs text-green-600 font-medium" data-id="element-1152">
                      In Forest
                    </span>
                  </div>
                </div>
                <div className="space-y-2 text-sm text-gray-600" data-id="element-1153">
                  <div data-id="element-1154">
                    <span className="font-medium" data-id="element-1155">Vessel:</span> {trip.vessel}
                  </div>
                  <div data-id="element-1156">
                    <span className="font-medium" data-id="element-1157">Zone:</span> {trip.zone}
                  </div>
                  <div data-id="element-1158">
                    <span className="font-medium" data-id="element-1159">Entry:</span> {trip.entryTime}
                  </div>
                  <div data-id="element-1160">
                    <span className="font-medium" data-id="element-1161">Elapsed:</span> {trip.elapsed}
                  </div>
                </div>
                <button className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50" data-id="element-1162">
                  <StopCircleIcon size={16} data-id="element-1163" />
                  End Trip
                </button>
              </div>)}
          </div>
        </div>}

      {activeTab === 'startTrip' && <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-3xl mx-auto" data-id="element-1164">
          <div className="flex items-center justify-between mb-6 border-b border-gray-200 pb-4" data-id="element-1165">
            <div data-id="element-1166">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-1167">
                Start Harvest Trip
              </h2>
              <p className="text-sm text-gray-600 mt-1" data-id="element-1168">
                Select an approved permit and provide trip details
              </p>
            </div>
            <div className="text-right" data-id="element-1169">
              <div className="text-sm text-gray-500" data-id="element-1170">
                Trip ID (Auto-generated)
              </div>
              <div className="font-mono font-bold text-lg text-[#1B5E20]" data-id="element-1171">
                TRP-HRV-
                {Math.floor(Math.random() * 10000).toString().padStart(4, '0')}
              </div>
            </div>
          </div>

          <form onSubmit={e => {
        e.preventDefault();
        alert('Trip started successfully! Status updated to Ongoing.');
        setActiveTab('tracking');
      }} className="space-y-6" data-id="element-1172">
            {/* Select Approved Permit */}
            <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1173">
              <h3 className="font-semibold text-gray-900 border-b border-gray-200 pb-2" data-id="element-1174">
                Select Approved Permit *
              </h3>
              <div data-id="element-1175">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1176">
                  Permit
                </label>
                <select required value={startTripForm.permitId} onChange={e => setStartTripForm({
              ...startTripForm,
              permitId: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1177">
                  <option value="" data-id="element-1178">Select an approved permit</option>
                  {approvedPermits.map(p => <option key={p.permitId} value={p.permitId} data-id="element-1179">
                      {p.permitId} — {p.beneficiaryName} ({p.type})
                    </option>)}
                </select>
              </div>

              {/* Permit Details Card */}
              {selectedPermit && <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-1180">
                  <div className="flex items-center justify-between" data-id="element-1181">
                    <h4 className="font-semibold text-gray-900" data-id="element-1182">
                      Permit Details
                    </h4>
                    <StatusBadge status={selectedPermit.status} data-id="element-1183" />
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-1184">
                    <div data-id="element-1185">
                      <span className="text-gray-600" data-id="element-1186">Beneficiary:</span>{' '}
                      <span className="font-medium" data-id="element-1187">
                        {selectedPermit.beneficiaryName}
                      </span>
                    </div>
                    <div data-id="element-1188">
                      <span className="text-gray-600" data-id="element-1189">Type:</span>{' '}
                      <span className="font-medium" data-id="element-1190">{selectedPermit.type}</span>
                    </div>
                    <div data-id="element-1191">
                      <span className="text-gray-600" data-id="element-1192">Duration:</span>{' '}
                      <span className="font-medium" data-id="element-1193">
                        {selectedPermit.duration}
                      </span>
                    </div>
                    <div data-id="element-1194">
                      <span className="text-gray-600" data-id="element-1195">Applied Date:</span>{' '}
                      <span className="font-medium" data-id="element-1196">
                        {selectedPermit.appliedDate}
                      </span>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Journey Details */}
            {selectedPermit && <div className="space-y-4" data-id="element-1197">
                <h3 className="font-semibold text-gray-900 border-b border-gray-200 pb-2" data-id="element-1198">
                  Journey Details
                </h3>
                <div data-id="element-1199">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1200">
                    Start Location (Station) *
                  </label>
                  <select required value={startTripForm.startLocation} onChange={e => setStartTripForm({
              ...startTripForm,
              startLocation: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1201">
                    <option value="" data-id="element-1202">Select Station</option>
                    <option value="chandpai" data-id="element-1203">Chandpai Station</option>
                    <option value="katka" data-id="element-1204">Katka Station</option>
                    <option value="dhangmari" data-id="element-1205">Dhangmari Station</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-1206">
                  <div data-id="element-1207">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1208">
                      Start Date *
                    </label>
                    <input type="date" required value={startTripForm.startDate} onChange={e => setStartTripForm({
                ...startTripForm,
                startDate: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1209" />
                  </div>
                  <div data-id="element-1210">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1211">
                      Start Time *
                    </label>
                    <input type="time" required value={startTripForm.startTime} onChange={e => setStartTripForm({
                ...startTripForm,
                startTime: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1212" />
                  </div>
                </div>
              </div>}

            <div className="flex justify-end gap-4 pt-6 border-t border-gray-200" data-id="element-1213">
              <button type="button" onClick={() => setActiveTab('tracking')} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium" data-id="element-1214">
                Cancel
              </button>
              <button type="submit" disabled={!selectedPermit} className="px-6 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1215">
                <PlayIcon size={18} data-id="element-1216" />
                Start Trip
              </button>
            </div>
          </form>
        </div>}

      {activeTab === 'history' && <DataTable columns={historyColumns} data={tripHistory} onDataChange={setTripHistory} searchPlaceholder="Search trip history..." data-id="element-1217" />}
    </div>;
}